import { useState } from "react";
import { Group, SegmentedControl, Select } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  AUDIENCE_TARGET_KINDS,
  type AudienceTarget,
  type AudienceTargetKind,
  type AudienceTargets,
} from "types/headsetReleaseTypes";
import { targetOptions } from "./releaseFormat";

interface AudienceTargetPickerProps {
  targets: AudienceTargets;
  value?: AudienceTarget;
  onChange: (target: AudienceTarget | undefined) => void;
  exclude?: AudienceTarget[];
}

export function AudienceTargetPicker({
  targets,
  value,
  onChange,
  exclude = [],
}: AudienceTargetPickerProps) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<AudienceTargetKind>(value?.kind ?? AUDIENCE_TARGET_KINDS[0]);
  const excluded = new Set(
    exclude.filter((target) => target.kind === kind).map((target) => target.id),
  );
  const options = targetOptions(kind, targets);

  const pickKind = (next: AudienceTargetKind) => {
    setKind(next);
    onChange(undefined);
  };

  return (
    <Group gap="sm" wrap="wrap">
      <SegmentedControl
        size="xs"
        aria-label={t("headsetReleases.targetKind")}
        value={kind}
        onChange={(next) => pickKind(next as AudienceTargetKind)}
        data={AUDIENCE_TARGET_KINDS.map((option) => ({
          value: option,
          label: t(`headsetReleases.target.${option}`),
        }))}
      />
      <Select
        size="xs"
        w={300}
        searchable
        clearable
        aria-label={t(`headsetReleases.target.${kind}`)}
        placeholder={t(`headsetReleases.searchTarget.${kind}`, { count: options.length })}
        value={value?.kind === kind ? value.id : null}
        onChange={(id) => onChange(id ? { kind, id } : undefined)}
        data={options
          .filter((option) => !excluded.has(option.id))
          .map((option) => ({
            value: option.id,
            label:
              option.orgCount !== undefined
                ? t("headsetReleases.targetWithCount", {
                    name: option.name,
                    count: option.orgCount,
                  })
                : option.name,
          }))}
        limit={50}
      />
    </Group>
  );
}
